/**
 * Pure spatial math (§3.2, §3.3). Minecraft axes: +X east, +Y up, +Z south (right-handed, like Web Audio).
 */
import type { GainConfig } from '../api/protocol';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const DEG = Math.PI / 180;

/**
 * Distance gain curve of §3.3:
 *   d <= ref           -> 1
 *   ref < d < R        -> ((R - d) / (R - ref)) ^ rolloff
 *   d >= R             -> 0
 */
export function distanceGain(d: number, cfg: GainConfig): number {
  const { refDistance: ref, hearRadius: r, rolloffExponent: k } = cfg;
  if (!Number.isFinite(d)) return 0;
  if (d <= ref) return 1;
  if (d >= r) return 0;
  return Math.pow((r - d) / (r - ref), k);
}

/** "Effetto prossimità" disattivo: blocchi di sfumatura prima di hearRadius (niente taglio netto). */
export const FLAT_FADE_BLOCKS = 2;

/**
 * Curve without proximity effect: full volume inside the hearing radius, then a short linear fade
 * over the last FLAT_FADE_BLOCKS blocks (the gain param is smoothed on top, so no clicks).
 *   d <= R - fade      -> 1
 *   R - fade < d < R   -> (R - d) / fade
 *   d >= R             -> 0
 */
export function flatGain(d: number, cfg: GainConfig): number {
  const r = cfg.hearRadius;
  if (!Number.isFinite(d) || d >= r) return 0;
  const fade = Math.min(FLAT_FADE_BLOCKS, r);
  if (d <= r - fade) return 1;
  return (r - d) / fade;
}

/** Gain for a distance with the proximity effect on (§3.3 curve) or off (flat + fade). */
export function proximityGain(d: number, cfg: GainConfig, proximity: boolean): number {
  return proximity ? distanceGain(d, cfg) : flatGain(d, cfg);
}

/** Minimum gain of team radio peers (`teamRadioMode = spatialFloor`, §4.3). */
export const RADIO_FLOOR_GAIN = 0.5;

/**
 * Listener forward vector from Minecraft yaw/pitch (degrees), §3.2.
 * With usePitch = false (default) only the yaw is used.
 */
export function yawToForward(yawDeg: number, pitchDeg = 0, usePitch = false): Vec3 {
  const y = yawDeg * DEG;
  if (!usePitch) return clean({ x: -Math.sin(y), y: 0, z: Math.cos(y) });
  const p = pitchDeg * DEG;
  return clean({ x: -Math.sin(y) * Math.cos(p), y: -Math.sin(p), z: Math.cos(y) * Math.cos(p) });
}

/**
 * Rotates a world offset (Minecraft axes, listener at the origin) into the listener space used
 * by the Web Audio listener left at its default orientation (forward -Z, up +Y, right +X).
 * Equivalent to orienting the AudioListener with yawToForward(), but portable (Firefox has no
 * AudioParam on the listener) and testable.
 */
export function toListenerSpace(off: Vec3, yawDeg: number): Vec3 {
  const y = yawDeg * DEG;
  const fx = -Math.sin(y);
  const fz = Math.cos(y);
  // right = forward x up = (-cos y, 0, -sin y)
  const rx = -Math.cos(y);
  const rz = -Math.sin(y);
  return clean({
    x: off.x * rx + off.z * rz,
    y: off.y,
    z: -(off.x * fx + off.z * fz),
  });
}

export function length(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

/** Wraps an angle to [-180, 180). */
export function wrapDeg(a: number): number {
  const w = ((((a + 180) % 360) + 360) % 360) - 180;
  return w;
}

/** Angular interpolation along the shortest path, result in [-180, 180). */
export function lerpAngle(a: number, b: number, f: number): number {
  return wrapDeg(a + wrapDeg(b - a) * f);
}

export function lerp(a: number, b: number, f: number): number {
  return a + (b - a) * f;
}

/** HRTF is unstable at distance ~0: closer than this the source is put right in front. */
export const MIN_PANNER_DISTANCE = 1.0;
export const FRONT: Vec3 = { x: 0, y: 0, z: -1 };

/** Position for the PannerNode in listener space (handles the d < 1 case of §3.3). */
export function pannerPosition(off: Vec3, yawDeg: number): Vec3 {
  if (length(off) < MIN_PANNER_DISTANCE) return { ...FRONT };
  return toListenerSpace(off, yawDeg);
}

function clean(v: Vec3): Vec3 {
  const c = (n: number) => (Math.abs(n) < 1e-12 ? 0 : n);
  return { x: c(v.x), y: c(v.y), z: c(v.z) };
}
